import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
    Check,
    ArrowUp,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoUrl from "@/assets/waiterix-logo.png";
import { Link } from "wouter";
import { useState } from "react";

// Product data
const products = [
    {
        id: "pos",
        name: "POS Terminal",
        description: "Counter or cashier station for order entry and checkout. Built for speed, reliability, and busy shifts.",
        image: "/assets/pos.jpeg",
        price: "$399",
        features: [
            "Fast checkout and receipts",
            "Supports common payment flows",
            "Designed for high volume operations",
        ],
        chips: ["Checkout", "Receipts", "High volume"],
        useCases: "Best for checkout at the counter, cashier workflows, and locations that need a reliable primary payment station.",
    },
    {
        id: "kds",
        name: "KDS",
        description: "Kitchen Display System that organizes tickets, reduces missed mods, and improves throughput.",
        image: "/assets/kds.png",
        price: "$199",
        features: [
            "Clear ticket columns and stages",
            "Designed for fewer errors under pressure",
            "Improves handoff and pacing",
        ],
        chips: ["Ticket stages", "Throughput", "Less rework"],
        useCases: "Best for kitchens that need clearer ticket flow, faster handoffs, and fewer errors during rush.",
    },
    {
        id: "mobile-ordering",
        name: "Mobile Ordering Station",
        description: "An iPad style station for counter pickup, self-serve ordering, or staff assisted ordering anywhere.",
        image: "/assets/mobile-ordering-station.jpeg",
        price: "$99",
        features: [
            "Ideal for quick service and events",
            "Compact footprint, easy to place",
            "Great for line busting",
        ],
        chips: ["Self-serve", "Line busting", "Events"],
        useCases: "Best for fast casual, popups, and line-busting. Place it at the counter, near pickup, or with staff for fast order capture.",
    },
    {
        id: "ai-device",
        name: "AI Device",
        description: "A guest-facing device for table-side experiences, smart ordering, and guided menu discovery.",
        image: "/assets/ai-device.jpeg",
        price: "$149",
        features: [
            "Supports conversational ordering flows",
            "Helps guests navigate the menu",
            "Reduces staff interruptions",
        ],
        chips: ["Table-side", "Guided ordering", "Upsells"],
        useCases: "Best for table-side experiences and guided ordering. Helps guests discover items, confirm choices, and move to checkout with less delay.",
    },
];

// Outcome cards data
const outcomeCards = [
    { value: "Faster", label: "order capture" },
    { value: "Cleaner", label: "kitchen tickets" },
    { value: "Less", label: "front of house friction" },
    { value: "More", label: "checkout speed" },
];

export default function Products() {
    const [showDemoDialog, setShowDemoDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState<'demo' | 'try'>('demo');
    const [demoForm, setDemoForm] = useState({
        name: "",
        email: "",
        phone: "",
    });

    const submitToHubSpot = async (data: { name: string; email: string; phone: string }, source: string) => {
        const portalId = "244843893";
        const formId = "b808aa91-1754-4c46-b1dc-c68e7f0912bb";
        const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

        const fields = [
            { name: "firstname", value: data.name },
            { name: "email", value: data.email },
            { name: "phone", value: data.phone }
        ];

        try {
            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fields,
                    context: {
                        pageUri: window.location.href,
                        pageName: document.title,
                        hutk: document.cookie.match(/hubspotutk=([^;]+)/)?.[1]
                    }
                })
            });
            console.log(`[HubSpot] Products lead submitted from ${source}`);
        } catch (err) {
            console.error("[HubSpot] Submission error:", err);
        }
    };

    const handleDemoSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Store lead data locally
        localStorage.setItem("waiterixDemoLead", JSON.stringify({ ...demoForm, ts: Date.now(), source: "products_" + dialogMode }));

        // Send to HubSpot
        submitToHubSpot(demoForm, `products_${dialogMode}`);

        // Close dialog
        setShowDemoDialog(false);
        // If demo mode, open Calendly
        if (dialogMode === 'demo') {
            window.open('https://calendly.com/kabir-waiterix/30min?back=1', '_blank');
        }
        // Reset form
        setDemoForm({ name: "", email: "", phone: "" });
    };

    const openDemoDialog = () => {
        setDialogMode('demo');
        setShowDemoDialog(true);
    };

    const scrollToSection = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation Header */}
            <header className="border-b glass backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-3">
                        <img src={logoUrl} alt="Waiterix" className="h-10 hover-lift smooth-transition" />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
                        <button onClick={() => scrollToSection("products")} className="hover:text-foreground transition-colors">Products</button>
                        <button onClick={() => scrollToSection("details")} className="hover:text-foreground transition-colors">Details</button>
                        <button onClick={() => scrollToSection("bundle")} className="hover:text-foreground transition-colors">Bundles</button>
                    </nav>

                    <div className="flex items-center gap-2">
                        <LanguageSelector />
                        <ThemeToggle />
                        <Button variant="outline" onClick={() => scrollToSection("bundle")}>
                            Request pricing
                        </Button>
                        <Button className="btn-glow shadow-glow hover-lift" onClick={openDemoDialog}>
                            Book a demo
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-16 md:py-24 border-b">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left Column - Text */}
                        <div className="space-y-6">
                            {/* Pill Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium">
                                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-primary to-orange-400 shadow-glow"></span>
                                Hardware built for Waiterix ordering, payments, and kitchen flow
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                                Waiterix devices, ready for{" "}
                                <span className="text-primary">real shifts.</span>
                            </h1>

                            <p className="text-lg text-muted-foreground max-w-2xl">
                                Borrowing the simplicity of modern restaurant POS pages, Waiterix keeps pricing clear and the setup minimal.
                                Choose a single device or build a complete front of house and kitchen workflow.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-wrap gap-4">
                                <Button size="lg" onClick={() => scrollToSection("products")} className="btn-glow shadow-glow hover-lift">
                                    View products
                                </Button>
                            </div>

                            <p className="text-sm text-muted-foreground">
                                Note: hardware pricing shown below. Software subscription sold separately.
                            </p>
                        </div>

                        {/* Right Column - Outcomes Card */}
                        <Card className="p-6 glass-card shadow-xl relative overflow-hidden">
                            <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-br from-primary/30 to-orange-400/20 rounded-full blur-3xl"></div>

                            <h3 className="font-bold mb-4 relative z-10">Common outcomes</h3>
                            <div className="grid grid-cols-2 gap-4 relative z-10">
                                {outcomeCards.map((outcome, index) => (
                                    <div key={index} className="p-4 rounded-xl bg-card/50 border">
                                        <div className="text-2xl font-bold text-primary">{outcome.value}</div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wide font-semibold mt-1">
                                            {outcome.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section id="products" className="py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Products and pricing</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl">
                            Pick the device that matches your workflow. Combine devices to run ordering, payments, and kitchen production with less friction.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {products.map((product) => (
                            <Card key={product.id} className="overflow-hidden flex flex-col">
                                <div className="p-4 border-b bg-card/50">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-48 object-contain rounded-xl"
                                    />
                                </div>
                                <div className="p-4 flex flex-col flex-1">
                                    <h3 className="font-bold text-lg mb-2">{product.name}</h3>
                                    <p className="text-sm text-muted-foreground mb-4">{product.description}</p>
                                    <ul className="text-sm text-muted-foreground space-y-2 mb-4 flex-1">
                                        {product.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="flex items-center justify-between pt-4 border-t">
                                        <div>
                                            <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Starting at</div>
                                            <div className="text-2xl font-bold">{product.price}</div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Details Section */}
            <section id="details" className="py-20 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">What each device is best for</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl">
                            Use these as building blocks. Most operators start with one station and expand into kitchen display and guest-facing devices.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {products.map((product) => (
                            <Card key={product.id} className="p-6 flex gap-6 items-start">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-44 h-32 object-contain rounded-xl border p-2 bg-card/50 flex-shrink-0 hidden sm:block"
                                />
                                <div className="flex-1">
                                    <h3 className="font-bold text-lg mb-2">{product.name}</h3>
                                    <p className="text-sm text-muted-foreground mb-4">{product.useCases}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {product.chips.map((chip, idx) => (
                                            <span
                                                key={idx}
                                                className="px-3 py-1.5 rounded-full border bg-primary/10 text-xs font-bold"
                                            >
                                                {chip}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Bundle CTA */}
                    <Card id="bundle" className="mt-12 p-8 glass-card relative overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-br from-primary/20 to-orange-400/10 rounded-full blur-3xl"></div>
                        <div className="flex flex-wrap items-center justify-between gap-6 relative z-10">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-bold mb-2">Build your Waiterix setup</h3>
                                <p className="text-muted-foreground">
                                    Tell us your concept and volume. We will recommend a device bundle and rollout plan that matches your workflow.
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                                    <ArrowUp className="h-4 w-4 mr-2" />
                                    Back to top
                                </Button>
                                <Button className="btn-glow shadow-glow hover-lift" onClick={openDemoDialog}>
                                    Book a demo
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t py-12 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center">

                        {/* Powered By */}
                        <div className="mb-8">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                                Powered by
                            </p>

                            <div className="flex justify-center items-center gap-8 opacity-80">
                                <img
                                    src="/assets/openai.png"
                                    alt="Powered by OpenAI"
                                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                                />
                                <img
                                    src="/assets/stripe.png"
                                    alt="Powered by Stripe"
                                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                                />
                                <img
                                    src="/assets/twilio.png"
                                    alt="Powered by Twilio"
                                    className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                                />
                            </div>
                        </div>

                        {/* Footer Links */}
                        <div className="flex justify-center gap-6 mb-4 text-sm">
                            <Link
                                href="/terms"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                data-testid="link-terms"
                            >
                                Terms & Conditions
                            </Link>
                            <span className="text-muted-foreground">•</span>
                            <Link
                                href="/privacy"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                data-testid="link-privacy"
                            >
                                Privacy Policy
                            </Link>
                        </div>

                        <p className="text-sm text-muted-foreground">
                            © 2025 Waiterix. All rights reserved.
                        </p>

                        {/* Disclaimer */}
                        <p className="text-xs text-muted-foreground/70 mt-6 max-w-6xl mx-auto leading-relaxed">
                            All information, services, products, and content on this website are provided for general informational purposes only and should not be considered professional advice. No rights or guarantees can be derived from the content on this site, and use of the website is governed by our Terms of Use. Reported results are based on either extrapolation or feedback from individual clients and may vary from one restaurant to another.
                        </p>

                        <p className="text-sm font-medium text-muted-foreground mt-2">
                            A product of Harmonia Enterprises LLC
                        </p>
                    </div>
                </div>
            </footer>


            {/* Demo Booking Dialog */}
            <Dialog open={showDemoDialog} onOpenChange={() => { }}>
                <DialogContent
                    className="sm:max-w-md [&>button]:hidden"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>
                            {dialogMode === 'demo' ? 'Book a Demo' : 'Enjoying the AI Waiter?'}
                        </DialogTitle>
                        <DialogDescription>
                            {dialogMode === 'demo'
                                ? "Fill in your details and we'll redirect you to schedule your demo."
                                : "Leave your details and we'll reach out to help you get started with Waiterix."}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleDemoSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="demo-name">Name</Label>
                            <Input
                                id="demo-name"
                                placeholder="Your name"
                                value={demoForm.name}
                                onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="demo-email">Email</Label>
                            <Input
                                id="demo-email"
                                type="email"
                                placeholder="you@example.com"
                                value={demoForm.email}
                                onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="demo-phone">Phone Number</Label>
                            <Input
                                id="demo-phone"
                                type="tel"
                                placeholder="+1 (555) 000-0000"
                                value={demoForm.phone}
                                onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                                required
                            />
                        </div>

                        <Button type="submit" className="w-full btn-glow shadow-glow">
                            {dialogMode === 'demo' ? 'Continue to Schedule' : 'Submit'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
